import React from "react";
import { DebuggerManager } from "@/stores/DebuggerManager";
import { comparisonTypeText } from "@common/targeting/audience-check";
import { Check, ChevronLeftIcon, CircleHelpIcon, CopyIcon, XIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useRef, useState } from "react";
import Switch from "../Switch";
import { TourPlaybackVisualizer } from "./TourPlaybackVisualizer";
import { CheckIcon } from "./CheckIcon";
import { CrossIcon } from "./CrossIcon";
import { camelCaseToTitleCase, replaceAll } from "@/lib/utils";
import { OnTrackCondition } from "@common/models/v2";

export interface TourDetailsModalProps {
  manager: DebuggerManager;
  isVisible: boolean;
  onClose: () => void;
  onBack: () => void;
  onTerminateTour: () => void;
  onStartTour: () => void;
  onStartTourFromStep: (stepId: string) => void;
}

export const TourDetailsModal = observer(
  ({
    manager,
    isVisible,
    onTerminateTour,
    onClose,
    onBack,
    onStartTour,
    onStartTourFromStep,
  }: TourDetailsModalProps) => {
    const tourPlaybackManager = manager.tourPlaybackManager;
    const isRunning = manager.runningGuide?.id === manager.selectedGuideId;
    const isAdvancedDetailsEnabled = manager.isAdvancedDetailsEnabled;
    const width = manager.tourDetailsModalWidth;

    const startXRef = useRef<number | null>(null);
    const startWidthRef = useRef<number | null>(null);

    const handleMouseDown = (e: React.MouseEvent) => {
      startXRef.current = e.clientX;
      startWidthRef.current = manager.tourDetailsModalWidth;
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (startXRef.current !== null && startWidthRef.current !== null) {
        const newWidth = startWidthRef.current - (e.clientX - startXRef.current);
        manager.setTourDetailsModalWidth(newWidth > 280 ? newWidth : 280);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "auto";
    };

    if (!isVisible) {
      return null;
    }

    return (
      <div className={`pyne-debugger-v2__tour-details-modal-container ${isVisible ? "visible" : "invisible"}`}>
        <div
          className="pyne-debugger-v2__tour-details-extend-handle"
          onMouseDown={handleMouseDown}
          style={{ right: width }}
        ></div>
        <div className="pyne-debugger-v2__tour-details-modal-content" style={{ width }}>
          <div className="pyne-debugger-v2__tour-details-modal-header">
            <div className="pyne-debugger-v2__tour-details-modal-header-left">
              <div className="pyne-debugger-v2__tour-details-modal-header-title">
                {manager.isTourDetailsModalBackButtonVisible && (
                  <button className="back-button" onClick={onBack}>
                    <ChevronLeftIcon style={{ width: 20, height: 20 }} />
                  </button>
                )}
                <h2>{manager.currentGuide?.title ?? "No guide"}</h2>
              </div>
              <div className="pyne-debugger-v2__tour-details-modal-header-actions">
                {isRunning ? (
                  <>
                    <button className="pyne-debugger-v2__terminate-tour-button" onClick={() => onTerminateTour()}>
                      Finish
                    </button>
                  </>
                ) : (
                  <>
                    <button className="pyne-debugger-v2__start-tour-button" onClick={() => onStartTour()}>
                      Start tour
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="pyne-debugger-v2__tour-details-modal-header-right">
              <button className="close-button" onClick={() => onClose()}>
                <XIcon style={{ width: 20, height: 20 }} />
              </button>
            </div>
          </div>
          <div className="pyne-debugger-v2__tour-details-modal-body">
            <div className="pyne-debugger-v2__tour-details-modal-body-header">
              <div className="pyne-debugger-v2__tour-details-modal-body-status">
                <div>Status</div>
                <div className={`badge ${isRunning ? "green" : ""}`}>{isRunning ? "Running" : "Inactive"}</div>
              </div>
              <div>
                <Switch
                  label="Advanced"
                  checked={isAdvancedDetailsEnabled}
                  onCheckedChange={(checked) => manager.setAdvancedDetailsEnabled(checked)}
                />
              </div>
            </div>
            {manager.isCurrentGuideRunning && manager.isRunningFlowExists && !manager.isRunningTourFlow && (
              <div className="pyne-debugger-v2__tour-playback-flow-picker-container">
                <div className="pyne-debugger-v2__tour-playback-flow-picker-title">Flow</div>
                <div className="pyne-debugger-v2__tour-playback-flow-picker-buttons-container">
                  <button
                    className={`pyne-debugger-v2__tour-playback-flow-button ${!manager.isRunningTourFlow ? "failed" : ""} ${manager.selectedFlowKey === "tour" ? "active" : ""}`}
                    onClick={() => manager.setSelectedFlowKey("tour")}
                  >
                    Tour {!manager.isRunningTourFlow && "(failed)"}
                  </button>
                  {manager.activeFlowKey && manager.activeFlowKey !== "tour" && (
                    <button
                      className={`pyne-debugger-v2__tour-playback-flow-button ${manager.selectedFlowKey === manager.activeFlowKey ? "active" : ""}`}
                      onClick={() => manager.setSelectedFlowKey(manager.activeFlowKey!)}
                    >
                      {camelCaseToTitleCase(manager.activeFlowKey)}
                    </button>
                  )}
                </div>
              </div>
            )}
            <div
              className={`pyne-debugger-v2__tour-playback-visualizer-container ${isAdvancedDetailsEnabled ? "mini" : ""}`}
            >
              <TourPlaybackVisualizer
                tourPlayback={manager.currentTourPlayback!}
                tourPlaybackManager={tourPlaybackManager}
                flowKey={manager.selectedFlowKey}
                activeFlowKey={manager.runningFlowKey}
                isRunning={manager.isCurrentGuideRunning}
                onStartTourFromStep={onStartTourFromStep}
                onStepClick={(stepId) => manager.selectStep(stepId)}
                selectedStepId={manager.selectedStepId}
                isStepOnTrack={(stepId: string) => manager.isStepOnTrack(stepId)}
                isStepTargetElementFound={(stepId: string) => manager.isStepTargetElementFound(stepId)}
                flowState={manager.selectedFlowState}
                allowStepEditing={manager.isAuthenticatedUser}
                onSelectorPickClick={(stepId) => manager.enableSelectorPickerMode(manager.currentGuide!.id, stepId)}
              />
            </div>
            {manager.pendingUpdatesForSelectedGuide.length > 0 && (
              <div className="pyne-debugger-v2__submit-updates-container">
                <button
                  className="pyne-debugger-v2__submit-updates-button"
                  onClick={() => manager.submitSelectorUpdates()}
                  disabled={manager.isSelectorUpdateLoading}
                >
                  {manager.isSelectorUpdateLoading
                    ? "Submitting..."
                    : `Submit ${manager.pendingUpdatesForSelectedGuide.length} Update${manager.pendingUpdatesForSelectedGuide.length > 1 ? "s" : ""}`}
                </button>
              </div>
            )}
            <div
              className={`pyne-debugger-v2__tour-advanced-details-container ${isAdvancedDetailsEnabled ? "visible" : "invisible"}`}
            >
              <AdvancedDetails manager={manager} />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

const AdvancedDetails = observer(({ manager }: { manager: DebuggerManager }) => {
  const tab = manager.advancedDetailsTab;

  return (
    <>
      <div className="pyne-debugger-v2__tour-advanced-details-container-header">
        <div className="pyne-debugger-v2__tour-advanced-details-tab-buttons">
          <button
            className={`pyne-debugger-v2__tour-advanced-details-tab-button ${tab === "guide" ? "active" : ""}`}
            onClick={() => manager.setAdvancedDetailsTab("guide")}
          >
            Guide
          </button>
          <button
            className={`pyne-debugger-v2__tour-advanced-details-tab-button ${tab === "targeting" ? "active" : ""}`}
            onClick={() => manager.setAdvancedDetailsTab("targeting")}
          >
            Targeting
          </button>
          <button
            className={`pyne-debugger-v2__tour-advanced-details-tab-button ${tab === "step" ? "active" : ""}`}
            onClick={() => manager.setAdvancedDetailsTab("step")}
          >
            Step
          </button>
          {manager.isDevModeEnabled && (
            <button
              className={`pyne-debugger-v2__tour-advanced-details-tab-button ${tab === "state" ? "active" : ""}`}
              onClick={() => manager.setAdvancedDetailsTab("state")}
            >
              State
            </button>
          )}
        </div>
      </div>
      <div className="pyne-debugger-v2__tour-advanced-details-container-body">
        {tab === "guide" && <GuideDetails manager={manager} />}
        {tab === "targeting" && <TargetingDetails manager={manager} />}
        {tab === "step" && <StepDetails manager={manager} />}
        {tab === "state" && <StateDetails manager={manager} />}
      </div>
    </>
  );
});

const GuideDetails = observer(({ manager }: { manager: DebuggerManager }) => {
  const guide = manager.currentGuide;
  const [isGuideIdCopied, setGuideIdCopied] = useState(false);
  const [isStartParamsCopied, setStartParamsCopied] = useState(false);

  if (!guide) return null;

  const copyGuideIDToClipboard = () => {
    navigator.clipboard.writeText(guide.id);
    setGuideIdCopied(true);
    setTimeout(() => setGuideIdCopied(false), 3000);
  };

  const copyStartParamsToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(manager.initialTourStateSnapshot, null, 2));
    setStartParamsCopied(true);
    setTimeout(() => setStartParamsCopied(false), 1000);
  };

  return (
    <div className="pyne-debugger-v2__details-container">
      <div>
        <div className="pyne-debugger-v2__details-title">Guide</div>
        <div className="pyne-debugger-v2__details-value">{guide.title ?? "Untitled"}</div>
      </div>
      <div>
        <div className="pyne-debugger-v2__details-title">
          Guide ID{" "}
          <span onClick={copyGuideIDToClipboard} style={{ display: "flex", alignItems: "center", marginLeft: "4px" }}>
            {isGuideIdCopied ? (
              <Check style={{ width: 14, height: 14, color: "rgb(56 158 13)" }} />
            ) : (
              <CopyIcon style={{ width: 14, height: 14, cursor: "pointer" }} />
            )}
          </span>
        </div>
        <div className="pyne-debugger-v2__details-value">{guide.id}</div>
      </div>
      <div>
        <div className="pyne-debugger-v2__details-title">Version</div>
        <div className="pyne-debugger-v2__details-value">{guide.tour_playback?.version}</div>
      </div>
      {manager.isDevModeEnabled && manager.tourStartParams && (
        <>
          <div>
            <div className="pyne-debugger-v2__details-title">
              Start params{" "}
              <span
                onClick={copyStartParamsToClipboard}
                style={{ display: "flex", alignItems: "center", marginLeft: "4px" }}
              >
                {isStartParamsCopied ? (
                  <Check style={{ width: 14, height: 14, color: "rgb(56 158 13)" }} />
                ) : (
                  <CopyIcon style={{ width: 14, height: 14, cursor: "pointer" }} />
                )}
              </span>
            </div>
            {Object.entries(manager.tourStartParams).map(([key, value]) => (
              <div key={key}>
                <div className="pyne-debugger-v2__details-subtitle">{key}</div>
                <div className="pyne-debugger-v2__details-subtitle-value">{value}</div>
              </div>
            ))}
          </div>
        </>
      )}
      {manager.isDevModeEnabled && manager.tourStartStrategy && (
        <>
          <div>
            <div className="pyne-debugger-v2__details-title">Last tour start strategy</div>
            <div>
              <div className="pyne-debugger-v2__details-subtitle">Type</div>
              <div className="pyne-debugger-v2__details-subtitle-value">{manager.tourStartStrategy.type}</div>
            </div>
            {"reason" in manager.tourStartStrategy && (
              <div>
                <div className="pyne-debugger-v2__details-subtitle">Reason</div>
                <div className="pyne-debugger-v2__details-subtitle-value">
                  {(manager.tourStartStrategy as any).reason}
                </div>
              </div>
            )}
            <div>
              <div className="pyne-debugger-v2__details-subtitle">Message</div>
              <div className="pyne-debugger-v2__details-subtitle-value">{manager.tourStartStrategy.message}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
});

const TargetingDetails = observer(({ manager }: { manager: DebuggerManager }) => {
  const matchEvaluation = manager.currentGuideTargetingDetails?.details;
  const isWhoConditionExists = matchEvaluation?.whoMatch && matchEvaluation.whoMatch?.evaluatedPredicates.length > 0;
  const isWhenConditionExists = matchEvaluation?.whenMatch && matchEvaluation.whenMatch?.predicates.length > 0;
  const isWhereConditionExists = matchEvaluation?.whereMatch && matchEvaluation.whereMatch?.predicates.length > 0;

  return (
    <div className="pyne-debugger-v2__details-container">
      <div>
        <div className="pyne-debugger-v2__details-title">Result</div>
        <div className="pyne-debugger-v2__details-value">{manager.currentGuideFormattedTargetingResult}</div>
      </div>
      <div>
        <div className="pyne-debugger-v2__details-title">Conditions</div>
        {isWhereConditionExists && (
          <>
            <div className="pyne-debugger-v2__targeting-title">Where</div>
            <div className="pyne-debugger-v2__targeting-condition-container">
              {matchEvaluation.whereMatch!.predicates.map((predicate) => {
                const isSuccess = predicate.result;
                return (
                  <>
                    <div
                      key={predicate.predicate.left_operand}
                      className={`pyne-debugger-v2__targeting-condition-item ${isSuccess ? "success" : "failed"}`}
                    >
                      <div className="pyne-debugger-v2__targeting-condition-item-icon">
                        {isSuccess ? <CheckIcon /> : <CrossIcon />}
                      </div>
                      <div style={{ overflow: "hidden" }}>
                        <div className="pyne-debugger-v2__targeting-condition-item-text-container">
                          <div className="pyne-debugger-v2__targeting-condition-item-text">
                            <span className="pyne-debugger-v2__targeting-condition-item-text-left">
                              {replaceAll(predicate.predicate.left_operand, "_", " ")}
                            </span>{" "}
                            <span className="pyne-debugger-v2__targeting-condition-item-text-middle">
                              {replaceAll(predicate.predicate.operator, "_", " ")}
                            </span>{" "}
                            <span className="pyne-debugger-v2__targeting-condition-item-text-right">
                              {predicate.predicate.right_operand}
                            </span>
                          </div>
                        </div>
                        <div className="pyne-debugger-v2__targeting-condition-item-text-sub">
                          <span className="pyne-debugger-v2__targeting-condition-item-text-sub-title">Actual: </span>
                          <span className="pyne-debugger-v2__targeting-condition-item-text-sub-value">
                            {predicate.value}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })}
            </div>
          </>
        )}
        {isWhoConditionExists && (
          <>
            <div className="pyne-debugger-v2__targeting-title">Who</div>
            <div className="pyne-debugger-v2__targeting-condition-container">
              {matchEvaluation.whoMatch!.evaluatedPredicates.map((predicate) => {
                const isSuccess = predicate.result;
                return (
                  <>
                    <div
                      key={predicate.predicate.attribute}
                      className={`pyne-debugger-v2__targeting-condition-item ${isSuccess ? "success" : "failed"}`}
                    >
                      <div className="pyne-debugger-v2__targeting-condition-item-icon">
                        {isSuccess ? <CheckIcon /> : <CrossIcon />}
                      </div>
                      <div>
                        <div className="pyne-debugger-v2__targeting-condition-item-text-container">
                          <div className="pyne-debugger-v2__targeting-condition-item-text">
                            <span className="pyne-debugger-v2__targeting-condition-item-text-left">
                              {predicate.predicate.attribute}
                            </span>{" "}
                            <span className="pyne-debugger-v2__targeting-condition-item-text-middle">
                              {comparisonTypeText[predicate.predicate.comparison]}
                            </span>{" "}
                            <span className="pyne-debugger-v2__targeting-condition-item-text-right">
                              {predicate.predicate.value}
                            </span>
                          </div>
                        </div>
                        <div className="pyne-debugger-v2__targeting-condition-item-text-sub">
                          <span className="pyne-debugger-v2__targeting-condition-item-text-sub-title">Actual: </span>
                          <span className="pyne-debugger-v2__targeting-condition-item-text-sub-value">
                            {predicate.value}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })}
            </div>
          </>
        )}
        {isWhenConditionExists && (
          <>
            <div className="pyne-debugger-v2__targeting-title">When</div>
            <div className="pyne-debugger-v2__targeting-condition-container">
              {matchEvaluation.whenMatch!.predicates.map((predicate) => {
                const isSuccess = predicate.result;
                return (
                  <>
                    <div
                      key={predicate.predicate.left_operand}
                      className={`pyne-debugger-v2__targeting-condition-item ${isSuccess ? "success" : "failed"}`}
                    >
                      <div className="pyne-debugger-v2__targeting-condition-item-icon">
                        {isSuccess ? <CheckIcon /> : <CrossIcon />}
                      </div>
                      <div>
                        <div className="pyne-debugger-v2__targeting-condition-item-text-container">
                          <div className="pyne-debugger-v2__targeting-condition-item-text">
                            <span className="pyne-debugger-v2__targeting-condition-item-text-left">
                              {replaceAll(predicate.predicate.left_operand, "_", " ")}
                            </span>{" "}
                            <span className="pyne-debugger-v2__targeting-condition-item-text-middle">
                              {replaceAll(predicate.predicate.operator, "_", " ")}
                            </span>{" "}
                            <span className="pyne-debugger-v2__targeting-condition-item-text-right">
                              {predicate.predicate.right_operand}
                            </span>
                          </div>
                        </div>
                        <div className="pyne-debugger-v2__targeting-condition-item-text-sub">
                          <span className="pyne-debugger-v2__targeting-condition-item-text-sub-title">Actual: </span>
                          <span className="pyne-debugger-v2__targeting-condition-item-text-sub-value">
                            {predicate.value}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

const StepDetails = observer(({ manager }: { manager: DebuggerManager }) => {
  const [copied, setCopied] = useState(false);

  const step = manager.selectedStep;
  if (!step)
    return (
      <div className="pyne-debugger-v2__details-container">
        <div className="pyne-debugger-v2__details-value">No step selected</div>
      </div>
    );

  const copyStepIDToClipboard = () => {
    navigator.clipboard.writeText(step.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const onTrackConditionResult = manager.currentStepOnTrackResult;
  const onTrackConditionDetailedResult = manager.currentStepDetailedOnTrackResult;

  const getOnTrackConditionResult = (condition: OnTrackCondition) => {
    return onTrackConditionDetailedResult?.find((c) => c.condition.isEqual(condition));
  };

  const isCheckpointCaptured = manager.isCheckpointCaptured(step.id);

  return (
    <div className="pyne-debugger-v2__details-container">
      <div>
        <div className="pyne-debugger-v2__details-title">
          Step ID
          <span onClick={copyStepIDToClipboard} style={{ display: "flex", alignItems: "center", marginLeft: "4px" }}>
            {copied ? (
              <Check style={{ width: 14, height: 14, color: "rgb(56 158 13)" }} />
            ) : (
              <CopyIcon style={{ width: 14, height: 14, cursor: "pointer" }} />
            )}
          </span>
        </div>
        <div className="pyne-debugger-v2__details-value">{step.id}</div>
      </div>
      <div>
        <div className="pyne-debugger-v2__details-title">Type</div>
        <div className="pyne-debugger-v2__details-value">{step.attributes.type}</div>
      </div>
      <div>
        <div className="pyne-debugger-v2__details-title">Is checkpoint</div>
        <div className="pyne-debugger-v2__details-value">
          {step.attributes.isCheckpoint ? "Yes" : "No"}
          <span style={{ display: "flex", alignItems: "center", marginLeft: "4px" }}>
            {step.attributes.isCheckpoint ? (
              <CheckIcon style={{ width: 14, height: 14, color: "rgb(56 158 13)" }} />
            ) : (
              <CrossIcon style={{ width: 14, height: 14, color: "rgb(239 68 68)" }} />
            )}
          </span>
        </div>
      </div>
      <div>
        <div className="pyne-debugger-v2__details-title">Is checkpoint captured</div>
        <div className="pyne-debugger-v2__details-value">
          {isCheckpointCaptured ? "Yes" : "No"}
          <span style={{ display: "flex", alignItems: "center", marginLeft: "4px" }}>
            {isCheckpointCaptured ? (
              <CheckIcon style={{ width: 14, height: 14, color: "rgb(56 158 13)" }} />
            ) : (
              <CrossIcon style={{ width: 14, height: 14, color: "rgb(239 68 68)" }} />
            )}
          </span>
        </div>
      </div>
      <div>
        <div className="pyne-debugger-v2__details-title">Start and end time</div>
        <div className="pyne-debugger-v2__details-value">
          {step.start} - {step.end}
        </div>
      </div>
      <div>
        <div className="pyne-debugger-v2__details-title">Media URL</div>
        <div className="pyne-debugger-v2__details-value">
          <a href={manager.currentSection?.avatarMediaUrl}>{manager.currentSection?.avatarMediaUrl}</a>
        </div>
      </div>
      <div>
        <div className="pyne-debugger-v2__details-title">Text</div>
        <div className="pyne-debugger-v2__details-value">{step.text}</div>
      </div>
      <div>
        <div className="pyne-debugger-v2__details-title">
          On track conditions
          <span style={{ display: "flex", alignItems: "center", marginLeft: "4px" }}>
            {onTrackConditionResult ? (
              <CheckIcon style={{ width: 14, height: 14, color: "rgb(56 158 13)" }} />
            ) : onTrackConditionResult === undefined ? (
              <CircleHelpIcon style={{ width: 14, height: 14 }} />
            ) : (
              <CrossIcon style={{ width: 14, height: 14, color: "rgb(239 68 68)" }} />
            )}
          </span>
        </div>
        <div className="pyne-debugger-v2__details-value">
          <div className="pyne-debugger-v2__on-track-condition-container">
            {step.attributes.onTrackConditions.isEmpty() && "No conditions specified!"}
            {step.attributes.onTrackConditions?.conditions
              .filter((c) => !c.isEmpty())
              .map((condition) => {
                const conditionResult = getOnTrackConditionResult(condition);
                const isUnknown = !conditionResult;

                return (
                  <div
                    key={condition.id}
                    className={`pyne-debugger-v2__on-track-condition-item ${isUnknown ? "unknown" : conditionResult?.isSatisfied ? "success" : "failed"}`}
                  >
                    <div className="pyne-debugger-v2__on-track-condition-item-icon">
                      {!isUnknown ? (
                        conditionResult.isSatisfied ? (
                          <CheckIcon />
                        ) : (
                          <CrossIcon />
                        )
                      ) : (
                        <CircleHelpIcon style={{ width: 16, height: 16 }} />
                      )}
                    </div>
                    <div>
                      <div className="pyne-debugger-v2__on-track-condition-item-text-container">
                        <div className="pyne-debugger-v2__on-track-condition-item-text">
                          <span className="pyne-debugger-v2__on-track-condition-item-text-left">
                            {condition.left_operand}
                          </span>{" "}
                          <span className="pyne-debugger-v2__on-track-condition-item-text-middle">
                            {replaceAll(condition?.operator ?? "", "_", " ")}
                          </span>{" "}
                          <span className="pyne-debugger-v2__on-track-condition-item-text-right">
                            {condition.right_operand}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
});

const StateDetails = observer(({ manager }: { manager: DebuggerManager }) => {
  if (!manager.tourPlaybackManager) {
    return (
      <div className="pyne-debugger-v2__details-container">
        <div className="pyne-debugger-v2__state-details">
          <div className="pyne-debugger-v2__details-value">Start a tour to see state details here.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pyne-debugger-v2__details-container">
      <div className="pyne-debugger-v2__state-details">
        <div>
          <div className="pyne-debugger-v2__details-title">Tour playback manager context</div>
          {manager.tourPlaybackFlowContext ? (
            <>
              {Object.entries(manager.tourPlaybackFlowContext).map(([key, value]) => {
                return (
                  <div key={`flow-context-${key}`}>
                    <div className="pyne-debugger-v2__details-subtitle">{key}</div>
                    <div className="pyne-debugger-v2__details-subtitle-value">{value}</div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="pyne-debugger-v2__details-subtitle-value">-</div>
          )}
        </div>
        <div>
          <div className="pyne-debugger-v2__details-title">Tour playback manager state</div>
          <div className="pyne-debugger-v2__details-value">{manager.tourPlaybackFlowStatus ?? "-"}</div>
        </div>
        <div>
          <div className="pyne-debugger-v2__details-title">Avatar manager state</div>
          <div className="pyne-debugger-v2__details-value">
            {manager.avatarState ? JSON.stringify(manager.avatarState, null, 2) : "-"}
          </div>
        </div>
        <div>
          <div className="pyne-debugger-v2__details-title">Avatar isPlaying</div>
          <div className="pyne-debugger-v2__details-value">{manager.avatarManager?.isPlaying ? "true" : "false"}</div>
        </div>
      </div>
    </div>
  );
});
